import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { COUNTRIES } from "../lib/countries";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { MailCheck } from "lucide-react";

export default function RegisterPage() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        name: "", email: "", password: "", countryName: "United States", dial: "+1", phone: "",
    });
    const [consent, setConsent] = useState({ terms: false, transactional: false, marketing: false });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [registered, setRegistered] = useState(null); // {email, dev_verify_url}

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onCountry = (name) => {
        const c = COUNTRIES.find((x) => x.name === name);
        setForm((f) => ({ ...f, countryName: name, dial: c ? c.dial : f.dial }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!consent.terms) {
            setError("You must agree to the Terms of Use and Privacy Policy to continue.");
            return;
        }
        setLoading(true);
        try {
            const data = await register({
                name: form.name,
                email: form.email,
                password: form.password,
                country: form.countryName,
                dial_code: form.dial,
                phone: form.phone,
                consent_terms: consent.terms,
                consent_transactional: consent.transactional,
                consent_marketing: consent.marketing,
            });
            setRegistered({ email: form.email, dev_verify_url: data.dev_verify_url });
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    if (registered) {
        return (
            <div className="max-w-md mx-auto px-4 sm:px-6 py-16" data-testid="register-confirm">
                <div className="border-2 border-ink p-8 text-center">
                    <MailCheck className="text-accent mx-auto" size={44} />
                    <h1 className="font-serif text-3xl font-black mt-4">Confirm your email</h1>
                    <p className="mt-3 text-sm text-muted-foreground">
                        We've sent a confirmation link to <span className="font-semibold text-ink">{registered.email}</span>.
                        Click it to activate your account and join the mailing list.
                    </p>
                    {registered.dev_verify_url && (
                        <div className="mt-5 border border-hair bg-muted p-4 text-left">
                            <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Demo mode — no email is sent</p>
                            <Link to={registered.dev_verify_url.replace(/^https?:\/\/[^/]+/, "")} data-testid="dev-verify-link" className="text-sm text-accent link-underline break-all">
                                Confirm your email now →
                            </Link>
                        </div>
                    )}
                    <button onClick={() => navigate("/")} data-testid="register-confirm-home" className="mt-6 w-full bg-ink text-white py-3 font-mono text-xs uppercase tracking-widest hover:bg-accent transition-colors duration-150">
                        Continue to the site
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-lg mx-auto px-4 sm:px-6 py-14" data-testid="register-page">
            <div className="border-2 border-ink p-8">
                <span className="font-mono text-xs uppercase tracking-widest text-accent">Free membership</span>
                <h1 className="font-serif text-3xl sm:text-4xl font-black mt-1">Join the mailing list</h1>
                <p className="mt-2 text-sm text-muted-foreground">Get market intelligence and personal-finance briefings by email and SMS.</p>

                <form onSubmit={submit} className="mt-8 space-y-5">
                    <div>
                        <label className="font-mono text-[11px] uppercase tracking-wider block mb-1">Full name</label>
                        <input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="register-name"
                            className="w-full border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent" />
                    </div>
                    <div>
                        <label className="font-mono text-[11px] uppercase tracking-wider block mb-1">Email</label>
                        <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="register-email"
                            className="w-full border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent" />
                    </div>
                    <div>
                        <label className="font-mono text-[11px] uppercase tracking-wider block mb-1">Password</label>
                        <input type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="register-password"
                            className="w-full border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent" />
                    </div>
                    <div>
                        <label className="font-mono text-[11px] uppercase tracking-wider block mb-1">Country</label>
                        <Select value={form.countryName} onValueChange={onCountry}>
                            <SelectTrigger data-testid="register-country" className="w-full border border-ink rounded-none px-3 py-2.5 text-sm h-auto focus:ring-1 focus:ring-accent">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-none border-2 border-ink max-h-72">
                                {COUNTRIES.map((c) => (
                                    <SelectItem key={c.code} value={c.name} className="rounded-none font-sans text-sm">
                                        {c.name} <span className="font-mono text-xs text-muted-foreground">({c.dial})</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="font-mono text-[11px] uppercase tracking-wider block mb-1">Phone number</label>
                        <div className="flex">
                            <span className="border border-r-0 border-ink px-3 flex items-center font-mono text-sm bg-muted" data-testid="register-dial">{form.dial}</span>
                            <input type="tel" required value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="555 123 4567" data-testid="register-phone"
                                className="flex-1 border border-ink px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent font-mono" />
                        </div>
                    </div>

                    {/* Consents */}
                    <div className="space-y-3 border-t border-hair pt-5">
                        <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-terms-label">
                            <Checkbox checked={consent.terms} onCheckedChange={(v) => setConsent((c) => ({ ...c, terms: !!v }))} data-testid="consent-terms" className="rounded-none border-ink mt-0.5" />
                            <span className="text-sm leading-snug">
                                I agree to the <Link to="/terms" className="text-accent link-underline">Terms of Use</Link> and <Link to="/privacy" className="text-accent link-underline">Privacy Policy</Link>. <span className="text-neg">*</span>
                            </span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-transactional-label">
                            <Checkbox checked={consent.transactional} onCheckedChange={(v) => setConsent((c) => ({ ...c, transactional: !!v }))} data-testid="consent-transactional" className="rounded-none border-ink mt-0.5" />
                            <span className="text-sm leading-snug text-muted-foreground">
                                I consent to receive transactional messages (account and service notifications) by email and SMS.
                            </span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-marketing-label">
                            <Checkbox checked={consent.marketing} onCheckedChange={(v) => setConsent((c) => ({ ...c, marketing: !!v }))} data-testid="consent-marketing" className="rounded-none border-ink mt-0.5" />
                            <span className="text-sm leading-snug text-muted-foreground">
                                I consent to receive marketing messages (newsletters and offers) by email and SMS.
                            </span>
                        </label>
                    </div>

                    {/* SMS legal disclosure */}
                    <p className="text-[11px] text-muted-foreground leading-relaxed bg-muted border border-hair p-3" data-testid="sms-disclosure">
                        By providing your phone number and checking the boxes above, you agree to receive text messages.
                        Consent is not a condition of purchase. Message and data rates may apply. Message frequency varies,
                        up to 4 messages per week. Reply STOP to opt out at any time, or HELP for help.
                    </p>

                    {error && <p className="text-sm text-neg border border-neg p-3" data-testid="register-error">{error}</p>}

                    <button type="submit" disabled={loading} data-testid="register-submit"
                        className="w-full bg-accent text-white py-3 font-mono text-xs uppercase tracking-widest hover:bg-ink transition-colors duration-150 disabled:opacity-50">
                        {loading ? "Creating account…" : "Create account"}
                    </button>
                </form>

                <p className="mt-6 text-sm text-muted-foreground">
                    Already a member? <Link to="/login" className="text-accent link-underline">Log in</Link>
                </p>
            </div>
        </div>
    );
}
